import { CSSProperties, useEffect, useState, useCallback, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import useBrand from 'Hooks/useBrand'
import useScreen from 'Hooks/useScreen'
import useCampaignBuilder from 'Hooks/useCampaignBuilder'
import { PlanType } from 'modules/Const'
import { formatNumberToDisplayType, styx, clsx, showToast } from 'modules/Utils'
import Colors from 'modules/Colors'
import ButtonSymphony from 'elements/ButtonSymphony'
import Icon from 'elements/Icon'
import CardSymphony from 'elements/CardSymphony'
import UnifiedPaymentForm from 'elements/UnifiedPaymentForm'
import { PaymentStatusCheckResponse, PlanComparison } from 'api/paymentsV2Api'
import { BillingCycle } from 'components/shareable/UpgradeToProModal/utils'
import LineItemsSummary from 'components/shareable/LineItemsSummary'


interface CampaignPlanSelectionViewProps {
    className?: string
    style?: CSSProperties
    onPlanSelected?: (planType: string) => void
    isCampaignBundle?: boolean
    overrideCampaignInput?: any
    onCancelPayment?: () => void; // Added prop for explicit cancellation/back
    showHeaders?: boolean;
    onPaid?: (result: PaymentStatusCheckResponse) => Promise<void>; // Added handler for parent components to handle post-payment logic
    existingCampaignId?: string; // Optional: For updating existing campaigns (agent campaigns)
}

// Plan name mapping for user-friendly display
const PLAN_DISPLAY_NAMES: Record<string, string> = {
    'starter': 'Starter',
    'growth': 'Growth',
    'team_v2': 'Team',
    'pro': 'Pro',
    'lite': 'Lite',
    'team': 'Team',
    'free': 'Free'
}

const CampaignCheckoutView = ({
    className = '',
    style = {},
    onPlanSelected,
    isCampaignBundle = false,
    overrideCampaignInput,
    onCancelPayment,
    showHeaders = true,
    onPaid,
    existingCampaignId
}: CampaignPlanSelectionViewProps) => {
    const { currentBrand, isPaidBrand } = useBrand()
    const { mobileView } = useScreen()
    const {
        campaignPlanDetailsData,
        isLoadingCampaignPlanDetails,
        getDataForSubmission,
        getAvailablePlansforCampaign,
        usesExpressSetup,
    } = useCampaignBuilder()

    const [step, setStep] = useState<'selection' | 'payment'>('selection')
    const [selectedPlan, setSelectedPlan] = useState<PlanComparison | null>(null)
    const [error, setError] = useState<string | null>(null)

    // UnifiedPaymentForm will handle its own payment intent management
    const transformedCampaignInput = overrideCampaignInput || getDataForSubmission()
    console.log('transformedCampaignInput', transformedCampaignInput)
    const userSelectedBudget = transformedCampaignInput.budget

    // Stable payment data to prevent infinite loops in UnifiedPaymentForm

    const paymentData = useMemo(() => ({
        type: (!isPaidBrand && selectedPlan?.planType !== 'free') ? 'campaign-with-subscription' as const : "campaign" as const,
        planType: selectedPlan?.planType as PlanType,
        billingCycle: BillingCycle.MONTHLY,
        campaignInput: transformedCampaignInput,
        ...(existingCampaignId && { campaignId: existingCampaignId }) // Include existing campaign ID for agent campaigns
    }), [isPaidBrand, selectedPlan?.planType, transformedCampaignInput, existingCampaignId])

    const reloadPlans = () => {
        if (!isLoadingCampaignPlanDetails) {
            getAvailablePlansforCampaign();
        }
    }

    // Fetch plan details when component mounts or budget changes
    useEffect(() => {
        reloadPlans();
    }, [])

    // Debounce plan reloads when the budget changes to prevent excessive API calls
    useEffect(() => {
        // Clear selected plan when budget changes to force re-selection with new data
        setSelectedPlan(null);

        const debounceTimeout = setTimeout(() => {
            reloadPlans();
        }, 300); // 300ms debounce interval – adjust if necessary

        return () => clearTimeout(debounceTimeout);
    }, [userSelectedBudget])

    // Reset to selection step when budget or express setup changes
    useEffect(() => {
        if (step === 'payment') {
            setStep('selection');
            setError(null);
        }
    }, [userSelectedBudget, usesExpressSetup()])

    // Auto-select recommended plan when data loads
    useEffect(() => {
        if (campaignPlanDetailsData && !selectedPlan) {
            const recommendedPlan = campaignPlanDetailsData.planComparisons.find(
                plan => plan.planType === campaignPlanDetailsData.recommendedPlan
            )
            setSelectedPlan(recommendedPlan || null)
        }
    }, [campaignPlanDetailsData, selectedPlan])

    // Handle plan selection
    const handlePlanSelect = useCallback((plan: PlanComparison) => {
        setSelectedPlan(plan)
        setError(null)

        if (onPlanSelected) {
            onPlanSelected(plan.planType)
        }
    }, [onPlanSelected])

    // Proceed to payment step
    const handleSubscribeClick = () => {
        if (!selectedPlan || !currentBrand?.id) {
            setError("Please select a plan.");
            return;
        }
        setError(null);
        setStep('payment')
    }


    const handleUnifiedPaymentSuccess = async (paymentResult: PaymentStatusCheckResponse) => {
        console.log('paymentResult', paymentResult)
        if (paymentResult.completed) {
            // Call the onPaid handler if provided (for parent component handling)
            if (onPaid) {
                await onPaid(paymentResult);
                // Early return to let parent handle the flow
                return;
            }
        } else {
            setError(paymentResult.error || "An error occurred during payment processing.");
            showToast({
                message: paymentResult.error || "Payment failed.",
                error: true,
                mobile: mobileView
            });
        }

        // Reset UI state
        setStep('selection')
        setSelectedPlan(null)
    };

    const handleBackToSelection = useCallback(() => {
        setStep('selection')
        setError(null)
        if (onCancelPayment) {
            onCancelPayment();
        }
    }, [onCancelPayment])

    const handleUnifiedPaymentError = (errorMessage: string) => {
        setError(errorMessage)
    }

    const renderPlanCard = (plan: PlanComparison, isRecommended: boolean = false) => {
        const isSelected = selectedPlan?.planType === plan.planType
        const displayName = PLAN_DISPLAY_NAMES[plan.planType] || plan.planType

        return (
            <div
                key={plan.planType}
                className={clsx('planCard', { 'selected': isSelected, 'recommended': isRecommended })}
                style={styx(sty.planCard, isSelected && sty.selectedPlanCard)}
                onClick={() => handlePlanSelect(plan)}
            >
                {isRecommended && (
                    <div className="recommendedBadge" style={sty.recommendedBadge}>
                        <Icon icon="star" size={12} style={sty.recommendedIcon} className="icon" />
                        Recommended
                    </div>
                )}

                <div className="planHeader" style={sty.planHeader}>
                    <div className="planTitle" style={sty.planTitle}>{displayName}</div>
                    <div className="planPricing" style={sty.planPricing}>
                        <span className="monthlyPrice" style={sty.monthlyPrice}>
                            {formatNumberToDisplayType(plan.monthlyPrice, 'currency')}
                        </span>
                        <span className="pricingPeriod" style={sty.pricingPeriod}>/month</span>
                    </div>
                </div>

                <div className="planFeatures" style={sty.planFeatures}>
                    <div className="featureText">
                        {(plan.takeRatePercentage * 100)}% ad fee • {plan.budgetLimit === Infinity ? 'Unlimited' : `≤$${formatNumberToDisplayType(plan.budgetLimit / 1000, 'number')}k`} budget
                    </div>
                </div>
            </div>
        )
    }

    const renderCheckoutSummary = () => {
        if (!selectedPlan) return null

        let title = selectedPlan.planType === PlanType.FREE ? 'Campaign Summary' : `${PLAN_DISPLAY_NAMES[selectedPlan.planType]} Plan Summary`

        console.log('selectedPlan', selectedPlan)
        return (
            <LineItemsSummary
                className="checkoutSummary"
                lineItems={selectedPlan.lineItems}
                totalAmount={selectedPlan.totalUpfrontCost}
                title={title}
                showTitle={true}
                style={sty.checkoutSummary}
            />
        )
    }

    const renderHeader = () => {
        if (!showHeaders) return null;
        return (
            <div className="planSelectionHeader" style={sty.header}>
                <div className="headerTitle" style={sty.headerTitle}>Choose Your Plan</div>
                <div className="headerSubtitle" style={sty.headerSubtitle}>
                    Select a plan to launch your campaign
                </div>
            </div>
        )
    }

    const renderSelectionStep = () => {
        if (isLoadingCampaignPlanDetails) {
            return (
                <CardSymphony
                    className="loadingCard"
                    isNoPadding
                    title="Calculating plan options..."
                    subtitle="Please wait while we determine the best pricing for your campaign"
                />
            )
        }

        if (!campaignPlanDetailsData) {
            return (
                <CardSymphony
                    className="errorCard"
                    isNoPadding
                    title="Unable to load plan options"
                    subtitle="Please try again or contact support if the issue persists"
                />
            )
        }

        return (
            <>
                {renderHeader()}

                {error && (
                    <div className="errorMessage" style={sty.errorMessage}>
                        <Icon icon="warning" size={16} className="errorMessageIcon" />
                        <span>{error}</span>
                    </div>
                )}

                <div className="plansGrid" style={mobileView ? sty.plansGridMobile : sty.plansGrid}>
                    {campaignPlanDetailsData.planComparisons.map(plan =>
                        renderPlanCard(plan, plan.planType === campaignPlanDetailsData.recommendedPlan)
                    )}
                </div>

                {renderCheckoutSummary()}

                <div className="planSelectionActions" style={sty.actions}>
                    <ButtonSymphony
                        className="subscribeButton"
                        variant="primary"
                        width="100%"
                        size="large"
                        onClick={handleSubscribeClick}
                        disabled={!selectedPlan}
                        style={sty.subscribeButton}
                    >
                        Proceed to Payment
                    </ButtonSymphony>
                </div>
            </>
        );
    };

    const renderPaymentStep = () => {

        if (!selectedPlan) {
            return (
                <div className="paymentError" style={sty.errorMessage}>
                    <Icon icon="warning" size={16} className="errorMessageIcon" />
                    <span>No plan selected. Please go back and select a plan.</span>
                    <ButtonSymphony
                        className="backButton"
                        onClick={handleBackToSelection}
                        style={{ marginTop: 10 }}
                    >
                        Back to Plan Selection
                    </ButtonSymphony>
                </div>
            )
        }

        return (
            <div className="paymentStep">
                <div className="paymentHeader" style={sty.header}>
                    <ButtonSymphony
                        className="backToPlanButton"
                        onClick={handleBackToSelection}
                        variant="text"
                        style={{ alignSelf: 'flex-start', marginBottom: 16 }}
                    >
                        ← Back to Plans
                    </ButtonSymphony>
                    <div className="headerTitle" style={sty.headerTitle}>
                        {selectedPlan.planType === 'free'
                            ? 'Submit Your Campaign'
                            : `Complete Your ${PLAN_DISPLAY_NAMES[selectedPlan.planType]} Subscription`
                        }
                    </div>
                    {/* <div className="headerSubtitle" style={sty.headerSubtitle}>
                        Total: {formatNumberToDisplayType(selectedPlan.totalUpfrontCost, 'currency')}
                    </div> */}
                </div>

                {error && (
                    <div className="errorMessage" style={sty.errorMessage}>
                        <Icon icon="warning" size={16} className="errorMessageIcon" />
                        <span>{error}</span>
                    </div>
                )}

                <UnifiedPaymentForm
                    shouldLoadIntent={true}
                    paymentData={paymentData}
                    onSuccess={handleUnifiedPaymentSuccess}
                    onError={handleUnifiedPaymentError}
                    onCancel={handleBackToSelection}
                    submitButtonText={selectedPlan.planType === 'free' ? 'Submit your Campaign' : `Subscribe to ${PLAN_DISPLAY_NAMES[selectedPlan.planType]}`}
                    showCancelButton={false}
                />
            </div>
        )
    }

    return (
        <div className={clsx('campaignPlanSelectionView', className)} style={styx(sty.container, style)}>
            {step === 'selection' ? renderSelectionStep() : renderPaymentStep()}
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        padding: 16,
        backgroundColor: '#ffffff',
        borderRadius: 12,
        border: '1px solid #e5e9f2',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    },

    header: {
        textAlign: 'center',
        marginBottom: 4,
    },

    headerTitle: {
        fontSize: 24,
        fontWeight: 600,
        color: '#1a1d29',
        marginBottom: 4,
    },

    headerSubtitle: {
        fontSize: 16,
        color: '#6b7280',
        lineHeight: 1.4,
    },

    plansGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 12,
        marginBottom: 16,
    },

    plansGridMobile: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        marginBottom: 16,
    },

    planCard: {
        position: 'relative',
        padding: 16,
        borderRadius: 8,
        border: '2px solid #e5e9f2',
        borderColor: Colors.greyLight,
        backgroundColor: '#ffffff',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    },

    selectedPlanCard: {
        borderColor: Colors.purpleLight,
        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.15)',
    },

    recommendedBadge: {
        position: 'absolute',
        top: -8,
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: Colors.purpleLight,
        color: '#ffffff',
        padding: '4px 8px',
        borderRadius: 12,
        fontSize: 12,
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        gap: 4,
    },

    recommendedIcon: {
        color: '#ffffff',
    },

    planHeader: {
        marginBottom: 12,
    },

    planTitle: {
        fontSize: 20,
        fontWeight: 600,
        color: '#1a1d29',
        marginBottom: 2,
    },

    planPricing: {
        display: 'flex',
        alignItems: 'baseline',
        gap: 4,
    },

    monthlyPrice: {
        fontSize: 28,
        fontWeight: 700,
        color: Colors.purpleLight,
    },

    pricingPeriod: {
        fontSize: 16,
        color: '#6b7280',
    },



    planFeatures: {
        fontSize: 12,
        color: '#6b7280',
        textAlign: 'center',
    },

    actions: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: 4,
    },

    paymentSummary: {
        marginBottom: 16,
    },

    summaryDetails: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: 16,
        color: Colors.greyDark,
        padding: '10px 14px',
        backgroundColor: Colors.greyLight,
        borderRadius: 8,
    },

    summaryPrice: {
        fontWeight: 500,
        color: Colors.purple,
    },

    paymentForm: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },

    paymentElementContainer: {
        padding: 12,
        border: `1px solid ${Colors.DARK_DIVIDER_LINE}`,
        borderRadius: 8,
        backgroundColor: 'white',
    },

    submitPaymentButton: {
        width: '100%',
    },

    errorMessage: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: 10,
        backgroundColor: '#fef2f2',
        borderRadius: 8,
        border: `1px solid ${Colors.red}`,
        color: Colors.red,
        fontSize: 14,
        marginBottom: 12,
    },

    paymentFormLoading: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 32,
        gap: 10,
        color: Colors.greyDark,
        fontSize: 14,
    },

    loadingIcon: {
        opacity: 0.7,
    },

    checkoutSummary: {
        marginTop: 16,
        border: '1px solid #e5e9f2',
        borderRadius: 8,
        backgroundColor: '#ffffff',
        overflow: 'hidden',
        padding: 16,
    },
}

export default CampaignCheckoutView 